<script lang="ts" module>
	export interface DescriptionRow {
		id: number;
		label: string;
		value: string;
		link?: string;
	}
</script>

<script lang="ts">
	import classNames from 'classnames';
	import { twMerge } from 'tailwind-merge';
	import { DevCartPrefill } from '../DevCartPrefill';
	import type { BookPdpTokens } from '../utils/getBookPdpTokensServer/getBookPdpTokensServer';
	import { getDetailsRows } from '../utils/getDetailsRows/getDetailsRows';
	import { Links } from '#lib/constants/links.js';
	import { AddToCart } from '#lib/features/checkout/components/AddToCart/index.js';
	import { getCheckoutContext } from '#lib/features/checkout/utils/getCheckoutContext/index.js';
	import { trackArtworkView } from '#lib/features/checkout/utils/trackArtworkView/index.js';
	import { PdpDetails } from '#lib/features/pdp/components/PdpDetails/index.js';
	import { getBookDisplayNames } from '#lib/features/publishing/utils/getBookDisplayNames/index.js';
	import { BookLabel } from '#lib/websites/publishing/components/BookLabel/index.js';
	import { PublishingRoutes } from '#lib/websites/publishing/constants/routes.js';
	import { getBookLabelText } from '#lib/websites/publishing/utils/getBookLabel/getBookLabel.js';
	import { ShopMixpanelEvents } from '#lib/websites/shop/constants/mixpanel-events.js';
	import { ShopRoutes } from '#lib/websites/shop/constants/routes.js';
	import type { ShopPdpBook } from '#lib/websites/shop/pages/ShopBookPdp/types.js';
	import { browser } from '$app/env';
	import { page } from '$app/state';
	import { ShareIcon } from '$global/assets/icons/ShareIcon';
	import { Accordion, AccordionItem } from '$global/components/Accordion';
	import { Button } from '$global/components/Button';
	import { createDialogStores } from '$global/components/Dialog';
	import { LinkButton } from '$global/components/LinkButton';
	import { Markdown } from '$global/components/Markdown';
	import { ShareDialog } from '$global/components/Share/ShareDialog';
	import { Txt } from '$global/components/Txt';
	import { PdpDescription } from '$global/features/pdp/components/PdpDescription';
	import { buildContentKey } from '$global/utils/contentKey';
	import { formatPrice } from '$global/utils/formatPrice';
	import type { PageData } from '$routes/shop/books/series/[slug=integer]/types';

	interface Props {
		seriesPage?: boolean | undefined;
		dataCy: string;
		selectedItemIndex: number;
		isLightboxOpen: boolean;
		class?: string;
		chooseYourOptionUrl?: string;
		getLink: (seriesPage: boolean | undefined, idOrSlug: string) => string;
	}

	let {
		chooseYourOptionUrl = PublishingRoutes.ChooseYourOption,
		seriesPage = undefined,
		dataCy,
		selectedItemIndex = $bindable(),
		isLightboxOpen = $bindable(),
		getLink,
		...rest
	}: Props = $props();

	let dataCyPrefix = $derived(`${dataCy}-description`);
	let book = $derived(page.data.book as ShopPdpBook);
	let series = $derived(page.data.series as PageData['series']);
	// The page's Markdown, parsed by its load.
	let tokens = $derived(page.data.bookTokens as BookPdpTokens | undefined);
	let shareIntro = $derived(
		`HENI Publishing: ${seriesPage ? series?.title : book?.title}`
	);
	let titleClass =
		'font-silka text-[1.75rem] font-[600] leading-[2.25rem] tracking-[-0.03125rem] text-gray-900 sm:text-[2.25rem] sm:leading-[2.5rem]';
	let range = $derived(page.data.range as PageData['range']);
	let detailsRows = $derived(getDetailsRows(book, series));
	let bookDisplayNames = $derived(getBookDisplayNames(book));
	let displayPeople = $derived(bookDisplayNames.people);
	// Separate from `displayPeople`: the byline stays filtered by
	// `show_author_artist`, while the About sections can cover every artist and
	// author when the book sets `show_all_bios`.
	let bioPeople = $derived(bookDisplayNames.bioPeople);

	// Global content key (HB-959) — the shared join key across commerce and News.
	// Books have no `medium`; `format` (hardback/paperback) is its analogue.
	//
	// `platform` is 'publishing' even though the event is namespaced SHOP/ (this
	// PDP lives under /shop/books and uses the shop checkout context): the event
	// prefix says which storefront the user was on, while `platform` says what
	// kind of product it is, which is what makes the key joinable across sites.
	let contentKey = $derived(
		buildContentKey({
			artist: bookDisplayNames.names,
			idValue: String(book?.id),
			idCollection: 'Publishing_Books',
			medium: book?.format,
			price: book?.product?.sale_price ?? book?.product?.price,
			currency: book?.product?.currency,
			release: book?.publication_date,
			category: book?.product?.product_categories
				?.map((category) => category?.ECM_Product_Category_id?.name)
				.filter(Boolean)
				.join(', '),
			platform: 'publishing',
		})
	);

	let cartItem = $derived({
		entityId: String(book?.id || ''),
		restricted: false,
		group: 'Publishing',
		groupId: 'Publishing',
		sku: book?.product?.sku || '',
		currency: book?.product?.currency || 'GBP',
		price: book?.product?.price || 0,
		salePrice: book?.product?.sale_price,
		name: book?.title || '',
		dropConfigId: null,
		contentKey,
	});

	// Fires once per book; the id guard keeps re-renders (lightbox, series
	// switching) from re-firing for the same book.
	let trackedBookId: string | undefined = $state();

	$effect(() => {
		if (book?.id && trackedBookId !== String(book.id)) {
			trackedBookId = String(book.id);
			trackArtworkView(ShopMixpanelEvents.ArtworkView, contentKey);
		}
	});

	const {
		cartItems: { cartItems },
	} = getCheckoutContext();

	let labelText = $derived(getBookLabelText(book));
	let addToCartDisabled = $derived(
		book?.product?.is_stock_limited &&
			(!browser || !!$cartItems[book.product?.id])
	);

	const dialogStores = createDialogStores();

	const {
		states: { open },
	} = dialogStores;

	const handleOpenShareDialog = () => {
		open.set(true);
	};
</script>

<div data-cy={dataCyPrefix} class={rest.class}>
	<div
		class={classNames(
			'full-w mt-6 flex justify-between gap-4',
			labelText ? 'items-center' : 'items-start'
		)}
	>
		{#if labelText}
			<BookLabel class="py-1">
				{labelText}
			</BookLabel>
		{:else}
			<Markdown
				component="h1"
				source={tokens?.titleTokens ?? []}
				dataCy={`${dataCyPrefix}-book-title`}
				class={twMerge('mb-2', titleClass)}
			/>
		{/if}

		<Button
			dataCy={`${dataCyPrefix}-share`}
			variant="secondary"
			size="sm"
			onclick={handleOpenShareDialog}
			icon
			class="mt-[0.3125rem] hidden h-[2.125rem] sm:block"
			classes={{ label: 'hidden' }}
		>
			{#snippet leading()}
				<ShareIcon class="h-4 w-4" />
			{/snippet}
		</Button>
	</div>

	{#if labelText}
		<Markdown
			component="h1"
			source={tokens?.titleTokens ?? []}
			dataCy={`${dataCyPrefix}-book-title`}
			class={twMerge('mt-4 mb-2', titleClass)}
		/>
	{/if}

	{#if !seriesPage && series?.title}
		<Txt data-cy={`${dataCyPrefix}-author-name`} variant="h6" class="mb-2">
			{series.title}
		</Txt>
	{/if}

	{#if book?.artist_name_override}
		<Txt
			data-cy={`${dataCyPrefix}-author-name`}
			variant="h6"
			class="mb-6 uppercase"
		>
			{book.artist_name_override}
		</Txt>
	{:else if displayPeople.length > 0}
		<Txt data-cy={`${dataCyPrefix}-author-name`} variant="h6" class="mb-6">
			{#each displayPeople as person, i}
				{@const filterParam = person.type === 'author' ? '1' : '2'}
				<a
					class="uppercase underline"
					data-cy={`${dataCyPrefix}-${person.type}-name-link`}
					href={`${ShopRoutes.Books}/?${filterParam}=${encodeURIComponent(
						person.full_name?.split(' ')?.slice(1)?.join(' ')?.trim() ||
							person.full_name
					)}&${filterParam}_type=3`}>{person.full_name}</a
				>{#if i < displayPeople.length - 1},{' '}{/if}
			{/each}
		</Txt>
	{/if}

	{#if range}
		<div class="mb-4">
			<Txt data-cy={`${dataCyPrefix}-price`} variant="h4">
				{#if range[0] === range[1] || !range[0] || !range[1]}
					{formatPrice(range[0] || range[1], 'GBP', true)}
				{:else}
					{`From ${formatPrice(range[0], 'GBP', true)}`}
				{/if}
			</Txt>
		</div>
	{:else if book.product?.in_stock}
		<div class="mb-4 flex items-baseline gap-2">
			<Txt data-cy={`${dataCyPrefix}-price`} variant="h4"
				>{formatPrice(
					book.product?.sale_price || book.product?.price,
					book.product?.currency,
					true
				)}
			</Txt>

			{#if book?.product?.sale_price}
				<Txt
					data-cy={`${dataCyPrefix}-striked-price`}
					variant="body1"
					class="text-gray-500 line-through"
					>{formatPrice(book.product?.price, book.product?.currency, true)}
				</Txt>
			{/if}
		</div>
	{/if}

	<div class="flex flex-col gap-2 sm:flex-row sm:gap-4">
		{#if !seriesPage}
			<div
				class={classNames('flex gap-2', {
					'sm:hidden': !book.product?.in_stock && range,
				})}
			>
				{#if book.product?.in_stock}
					<AddToCart
						dataCy={dataCyPrefix}
						{cartItem}
						disabled={addToCartDisabled}
						productId={book.product?.id}
						quantity={1}
						class={classNames(
							'min-w-[calc(100%-3rem)] sm:w-full',
							addToCartDisabled ? 'sm:mb-0' : 'sm:mb-6'
						)}
					>
						{#if book.product.can_pre_order}
							Pre-order
						{:else}
							Add to cart
						{/if}
					</AddToCart>
				{:else if !range}
					<LinkButton
						dataCy={`${dataCyPrefix}-contact-us`}
						size="md"
						variant="secondary"
						href={`${Links.ContactUs}?enquiryType=HENI%20Publishing`}
						newTab
						class={classNames(addToCartDisabled ? 'sm:mb-0' : 'sm:mb-6')}
					>
						contact us
					</LinkButton>
				{/if}

				<Button
					dataCy={`${dataCyPrefix}-share`}
					variant="secondary"
					size="sm"
					onclick={handleOpenShareDialog}
					icon
					class={classNames('h-[2.5rem] w-[2.5rem] sm:hidden', {
						'mb-6': !series,
					})}
				>
					<ShareIcon class="h-4 w-4" />
				</Button>
			</div>
		{/if}

		{#if addToCartDisabled && browser}
			<Txt
				dataCy={`${dataCyPrefix}-helper`}
				variant="body3"
				class={' text-gray-500 sm:hidden'}
			>
				Only one can be purchased
			</Txt>
		{/if}

		{#if series}
			<div
				class={classNames(
					'mb-6 flex w-full gap-2 sm:w-auto',
					addToCartDisabled && browser ? 'sm:mb-2' : 'sm:mb-6'
				)}
			>
				<LinkButton
					size="md"
					href={`${chooseYourOptionUrl}/${series.id}`}
					dataCy={`${dataCyPrefix}-change`}
					variant={range ? 'primary' : 'secondary'}
					class={twMerge(
						'w-full sm:w-auto [&>button]:w-full sm:[&>button]:w-auto',
						classNames({ 'min-w-[calc(100%-3rem)] sm:w-auto': seriesPage })
					)}
				>
					{`${range ? 'Select' : 'Change'} ${series.option_label || 'Cover'}`}
				</LinkButton>

				{#if seriesPage}
					<Button
						dataCy={`${dataCyPrefix}-share`}
						variant="secondary"
						size="sm"
						onclick={handleOpenShareDialog}
						icon
						class={classNames('min-h-[2.5rem] min-w-[2.5rem] sm:hidden', {
							'mb-6': !series,
						})}
					>
						<ShareIcon class="h-4 w-4" />
					</Button>
				{/if}
			</div>
		{/if}
	</div>

	{#if addToCartDisabled && browser}
		<Txt
			dataCy={`${dataCyPrefix}-helper`}
			variant="body3"
			class="mb-6 hidden text-gray-500 sm:block"
		>
			Only one can be purchased
		</Txt>
	{/if}

	<div class="border-b border-gray-200"></div>
	<Accordion>
		<AccordionItem
			defaultOpen
			dataCy={`${dataCyPrefix}-description`}
			title="Description"
		>
			<PdpDescription
				{dataCy}
				class="my-4 lg:mt-6"
				longDescription={book?.description || ''}
				shortDescription={null}
				longDescriptionTokens={tokens?.descriptionTokens}
				shortDescriptionTokens={[]}
				markdown
				clamp
			/>
		</AccordionItem>
	</Accordion>

	<div class="border-b border-gray-200"></div>

	<Accordion title="Book Details">
		<AccordionItem dataCy={`${dataCyPrefix}-details`} title="Book Details">
			<PdpDetails
				bind:selectedItemIndex
				bind:isLightboxOpen
				{dataCy}
				href={null}
				rows={detailsRows}
				class="mt:4 mb-6 lg:mt-6"
			/>
		</AccordionItem>
	</Accordion>

	{#each bioPeople as person, index}
		{#if person.biography}
			<div class="border-b border-gray-200"></div>

			<Accordion
				title="About the {person.type === 'author' ? 'Author' : 'Artist'}"
			>
				<AccordionItem
					dataCy={`${dataCyPrefix}-about-${person.type}`}
					title="About the {person.type === 'author'
						? 'Author'
						: 'Artist'}{bioPeople.length > 1 ? ` - ${person.full_name}` : ''}"
				>
					<PdpDescription
						{dataCy}
						class="mt:4 mb-6 lg:mt-6"
						longDescription={person.biography}
						shortDescription={null}
						longDescriptionTokens={tokens?.biographyTokens[index]}
						shortDescriptionTokens={[]}
						markdown
					/>
				</AccordionItem>
			</Accordion>
		{/if}
	{/each}

	{#if book.about_editor}
		<div class="border-b border-gray-200"></div>

		<Accordion title="About the Editor">
			<AccordionItem
				dataCy={`${dataCyPrefix}-about-editor`}
				title="About the Editor"
			>
				<PdpDescription
					{dataCy}
					class="mt:4 mb-6 lg:mt-6"
					longDescription={book?.about_editor}
					shortDescription={null}
					longDescriptionTokens={tokens?.aboutEditorTokens}
					shortDescriptionTokens={[]}
					markdown
				/>
			</AccordionItem>
		</Accordion>
	{/if}

	{#if book?.contributing_artists}
		<div class="border-b border-gray-200"></div>
		<Accordion>
			<AccordionItem
				dataCy={`${dataCyPrefix}-contributing-artists`}
				title="Contributing Artists"
			>
				<PdpDescription
					{dataCy}
					class="my-4 lg:mt-6"
					longDescription={book?.contributing_artists}
					shortDescription={null}
					longDescriptionTokens={tokens?.contributingArtistsTokens}
					shortDescriptionTokens={[]}
					markdown
					clamp
				/>
			</AccordionItem>
		</Accordion>
	{/if}

	{#if book?.contributors?.trim?.()?.length}
		<div class="border-b border-gray-200"></div>

		<Accordion title="Contributors">
			<AccordionItem
				dataCy={`${dataCyPrefix}-contributors`}
				title="Contributors"
			>
				<PdpDescription
					{dataCy}
					class="mt:4 lg:mt-6"
					longDescription={book.contributors}
					shortDescription={null}
					longDescriptionTokens={tokens?.contributorsTokens}
					shortDescriptionTokens={[]}
					markdown
				/>
			</AccordionItem>
		</Accordion>
	{/if}

	{#if book?.press_coverage_content}
		<div class="border-b border-gray-200"></div>
		<Accordion>
			<AccordionItem
				dataCy={`${dataCyPrefix}-press-coverage`}
				title="Press Coverage"
			>
				<PdpDescription
					{dataCy}
					class="my-4 lg:mt-6"
					shortDescription={book?.press_coverage_content}
					longDescription=""
					shortDescriptionTokens={tokens?.pressCoverageTokens}
					longDescriptionTokens={[]}
					markdown
				/>
			</AccordionItem>
		</Accordion>
	{/if}

	{#if book?.product?.product_categories?.length}
		<div class="mt-5 flex items-start sm:mt-10">
			<Txt variant="h6" class="mr-4 pt-0.5">Categories:</Txt>
			<div class="flex flex-row flex-wrap gap-4">
				{#each book.product.product_categories as category}
					{@const categoryName = category?.ECM_Product_Category_id?.name || ''}
					{#if categoryName}
						<a
							data-cy={`${dataCyPrefix}-category`}
							href={`${ShopRoutes.Books}?3=${encodeURIComponent(
								categoryName
							)}&3_type=3`}
							class={classNames('shrink-0 rounded-lg bg-gray-200 px-3 py-1.5')}
						>
							<Txt variant="label3">
								{categoryName}
							</Txt>
						</a>
					{/if}
				{/each}
			</div>
		</div>
	{/if}
</div>

<DevCartPrefill slug={book?.slug} productId={book?.product?.id} {cartItem} />

<ShareDialog
	link={getLink(
		seriesPage,
		seriesPage ? String(series?.id ?? '') : (book?.slug ?? '')
	)}
	{dialogStores}
	intro={shareIntro}
	dataCy={dataCyPrefix}
	showText={false}
	showTwitter
/>
